import * as React from "react";

import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { Icon } from "../icon";
import { cn } from "../utils";

export interface InfoHintProps {
  /** The explanatory copy — a sentence or two, tucked one click away. */
  children: React.ReactNode;
  /** Accessible label for the trigger. */
  label?: string;
  /** Class on the `(i)` trigger button. */
  className?: string;
  /** Class on the popover content. */
  contentClassName?: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
}

/**
 * A small `(i)` affordance that tucks a block of explanatory copy into a click
 * popover, so a title/count/value stays clean and the prose is one click away.
 *
 * The house pattern for "this paragraph of helper text shouldn't crowd the
 * surface" — pair it with a `<DataTable title>`, a `PageHeader` title, or a
 * stat label instead of dropping a muted paragraph inline. Click (not hover) so
 * the copy is dismissible and selectable, which a tooltip can't do.
 */
export function InfoHint({
  children,
  label = "More info",
  className,
  contentClassName,
  side = "top",
  align = "center",
}: InfoHintProps) {
  return (
    <Popover>
      <PopoverTrigger
        aria-label={label}
        className={cn(
          "inline-flex size-4 shrink-0 items-center justify-center rounded-full align-middle text-muted-foreground/70 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          className,
        )}
      >
        <Icon name="info" size="xs" aria-hidden />
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        className={cn(
          "w-72 type-body leading-body font-normal text-muted-foreground",
          contentClassName,
        )}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
